import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FileErrorModalComponent } from "@components/file-error-modal/file-error-modal.component";
import { ImageUploadModalComponent } from "@components/image-upload-modal/image-upload-modal.component";
import { CameraModalComponent } from "@components/image-upload-overlay/camera-modal/camera-modal.component";
import { ToggleItemTextVisibilityButtonComponent } from "@components/toggle-item-text-visibility-button/toggle-item-text-visibility-button.component";
import { ToggleLockButtonComponent } from "@components/toggle-lock-button/toggle-lock-button.component";
import { ToggleVisibilityButtonComponent } from "@components/toggle-visibility-button/toggle-visibility-button.component";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { ProjectImageData, ProjectImageService, SyncState } from "@domain/project/project-image.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ModalService } from "@odx/angular/components/modal";
import { FloorplanMenuItemButtonsComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-item-buttons/floorplan-menu-item-buttons.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ProjectImageDataWithUrl } from "@project/project-details/image-upload/image-upload.component";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { forkJoin, map, switchMap } from "rxjs";

@Component({
  selector: "app-floorplan-image-menu",
  templateUrl: "./floorplan-image-menu.component.html",
  styleUrls: ["./floorplan-image-menu.component.scss"],
  imports: [
    UiKitModule,
    FloorplanMenuItemButtonsComponent,
    ToggleLockButtonComponent,
    ToggleVisibilityButtonComponent,
    ToggleItemTextVisibilityButtonComponent,
  ],
})
export class FloorplanImageMenuComponent implements OnInit, OnDestroy {
  private floorplanService = inject(FloorplanService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private projectImageService = inject(ProjectImageService);

  protected floorplan: Floorplan;
  protected images: ProjectImageDataWithUrl[] = [];

  protected readonly tooltipTextUploadImage: string = $localize`:@@floorplan.button.uploadImage:Foto hochladen`;
  protected readonly tooltipTextTakeImage: string = $localize`:@@floorplan.button.takeImage:Foto aufnehmen`;

  private readonly modalService = inject(ModalService);
  private dragDropService = inject(FloorplanDragDropService);

  businessScope!: BusinessScope;
  protected readonly BusinessScope = BusinessScope;

  constructor() {
    const floorplanService = this.floorplanService;

    this.floorplan = floorplanService.selectedFloorplan;

    this.userService.businessScope$.subscribe((scope) => {
      this.businessScope = scope;
    });
  }

  ngOnInit(): void {
    if (this.floorplanService.selectedFloorplan.project.id) {
      this.loadImagePreviews(this.floorplanService.selectedFloorplan.project.id);
    }
  }

  ngOnDestroy(): void {
    this.images.forEach((img) => {
      if (img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
  }

  addImage(projectImage: ProjectImageMetadata) {
    this.floorplanService.addImage(projectImage);
  }

  dragImage(projectImage: ProjectImageMetadata) {
    this.dragDropService.setDragged({ type: DraggedItemType.IMAGE, config: projectImage });
  }

  openCamera() {
    const modalRef = this.modalService.open(CameraModalComponent);
    modalRef.onClose$.subscribe((image) => {
      this.projectImageService.convertHeicImages([image]).subscribe((images) => {
        const convertedImage = images[0];

        if (this.businessScope === BusinessScope.FGDS) {
          this.projectImageService.createPreviewImage(convertedImage).subscribe((previewBlob) => {
            const projectImage = {
              blob: convertedImage,
              previewBlob,
              imageMetadata: ProjectImageMetadata.create($localize`:@@imageUpload.defaultName:Platzhalter`),
              projectId: this.floorplanService.selectedFloorplan.project.id,
              syncState: SyncState.NEW,
            };

            this.uploadImages([projectImage]);
          });
        }

        if (this.businessScope === BusinessScope.MONITORING) {
          const uploadImageModalRef = this.modalService.open(ImageUploadModalComponent, {
            data: {
              imageUrl: URL.createObjectURL(convertedImage),
              name: convertedImage.name,
            },
          });

          uploadImageModalRef.onClose$.subscribe((result) => {
            this.projectImageService.createPreviewImage(convertedImage).subscribe((previewBlob) => {
              const projectImage = {
                blob: convertedImage,
                previewBlob,
                imageMetadata: ProjectImageMetadata.create(result.name, result.description, result.tags ?? undefined),
                projectId: this.floorplanService.selectedFloorplan.project.id,
                syncState: SyncState.NEW,
              };
              this.uploadImages([projectImage]);
            });
          });
        }
      });
    });
  }

  onImageFileSelect(event: any) {
    const invalidFiles = this.projectImageService.checkIfTypeIsWhitelisted(event.target.files);

    if (invalidFiles.length > 0) {
      const unsupportedFiles = this.projectImageService.filterUnsupportedFileTypes(invalidFiles);
      const completeErrorMessage = $localize`:@@floorplan.modal.imageUpload.invalidFiles.errorMessage:Das Dateiformat von ${unsupportedFiles} wird nicht unterstützt.`;
      this.openUploadErrorModal(completeErrorMessage, event);
      return;
    }

    this.projectImageService.convertHeicImages([...event.target.files]).subscribe((images) => {
      // reset value to allow for uploading the same file multiple times
      event.target.value = null;

      if (this.businessScope === BusinessScope.FGDS) {
        const projectImages$ = images.map((image) =>
          this.projectImageService.createPreviewImage(image).pipe(
            map((previewBlob) => ({
              blob: image,
              previewBlob: previewBlob,
              imageMetadata: ProjectImageMetadata.create(image.name),
              projectId: this.floorplanService.selectedFloorplan.project.id,
              syncState: SyncState.NEW,
            })),
          ),
        );
        forkJoin(projectImages$).subscribe((projectImages) => {
          this.uploadImages(projectImages);
        });
      }

      if (this.businessScope === BusinessScope.MONITORING) {
        const modalRef = this.modalService.open(ImageUploadModalComponent, {
          data: {
            imageUrl: URL.createObjectURL(images[0]),
            name: images[0].name,
          },
        });

        modalRef.onClose$
          .pipe(
            switchMap((result) =>
              this.projectImageService.createPreviewImage(images[0]).pipe(
                map((previewBlob) => ({
                  blob: images[0],
                  previewBlob: previewBlob,
                  imageMetadata: ProjectImageMetadata.create(images[0].name, result.description, result.tags ?? undefined),
                  projectId: this.floorplanService.selectedFloorplan.project.id,
                  syncState: SyncState.NEW,
                })),
              ),
            ),
          )
          .subscribe((image) => {
            this.uploadImages([image]);
          });
      }
    });
  }

  protected updateImagesItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updateImagesItemTextVisibility(visible);
  }

  private uploadImages(projectImages: ProjectImageData[]) {
    const project = this.floorplanService.selectedFloorplan.project;
    this.projectImageService.saveImagesToIndexedDB(projectImages, project).subscribe(() => {
      this.loadImagePreviews(this.floorplanService.selectedFloorplan.project.id);
    });
  }

  private openUploadErrorModal(errorMessage: string, event: any) {
    this.modalService
      .open(FileErrorModalComponent, {
        data: {
          text: errorMessage,
        },
      })
      .onClose$.subscribe(() => {
        event.target.value = null;
      });
  }

  private loadImagePreviews(projectId: string) {
    this.projectImageService.getImagesFromIndexedDB(projectId).subscribe({
      next: (imagesFromDB) => {
        this.images.forEach((img) => {
          if (img.previewUrl) {
            URL.revokeObjectURL(img.previewUrl);
          }
        });

        this.images = imagesFromDB
          .filter((imageData) => imageData.syncState !== SyncState.DELETED)
          .map((imageData) => ({
            ...imageData,
            previewUrl: imageData.previewBlob ? URL.createObjectURL(imageData.previewBlob) : undefined,
          }));
      },
      error: (err) => {
        console.error("Error while loading images from indexedDB:", err);
      },
    });
  }
}
